import { AfterContentInit, Component, Input, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { untilDestroyed } from '@app/core';
import { AuthenticationService, DocumentService, ErrorHandlerService, NotyService, PhoneOtpProviderService, PhoneOtpProvider, VerificationService } from '@app/services';
import { SecurityVerificationEnum, SecurityVerificationType } from '@app/models';
import { LoaderService } from '@app/shared';
import { TranslateService } from '@ngx-translate/core';
import { CountdownTimerService } from '@app/services/countdowntimer.service';

export type ModalResult = SecurityVerificationType;
export interface ModalPublicProperties {
  verification: SecurityVerificationType;
  documentId: string;
}

@Component({
  selector: 'app-verification-sms-modal',
  templateUrl: './verification-sms-modal.component.html',
  styleUrls: ['./verification-sms-modal.component.scss'],
  standalone: false
})
export class VerificationSmsModalComponent implements ModalPublicProperties, AfterContentInit, OnDestroy {
  @Input() verification: SecurityVerificationType;
  @Input() signatureId: any;
  @Input() documentId: string;
  form!: UntypedFormGroup;
  isLoading = false;
  isVerifyingToken = false;
  isInvalidToken = false;
  token = new FormControl('');
  verifyPhoneExisting = false;
  formattedTime = '';
  currentPhoneOtpProvider!: PhoneOtpProvider;

  constructor(
    public modal: NgbActiveModal,
    public whatsAppTimer: CountdownTimerService,
    public phoneOtpProvider: PhoneOtpProviderService,
    private formBuilder: UntypedFormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private documentService: DocumentService,
    private notyService: NotyService,
    private authenticationService: AuthenticationService,
    private loaderService: LoaderService,
    private translateService: TranslateService,
    private verificationService: VerificationService
  ) {
    this.form = this.formBuilder.group({ phoneNumber: '' });
  }

  ngOnDestroy() {}

  ngAfterContentInit() {
    this.currentPhoneOtpProvider = this.phoneOtpProvider.primaryProviderForPhone(this.verification?.verify_phone);

    if (this.verification && this.verification.type === SecurityVerificationEnum.Sms) {
      if (this.verification.verify_phone) {
        this.verifyPhoneExisting = true;
        this.sendSecurityCode();
      }
    }
    this.timeOutForWhatsappVerification();
  }

  updateSecurityVerificationMutation() {
    const verifyPhone = this.form.get('phoneNumber').value;

    this.isLoading = true;
    this.loaderService.show();
    this.verificationService
      .updateSecurityVerification({ id: this.verification.id, securityVerification: { type: SecurityVerificationEnum.Sms, verify_phone: verifyPhone } })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.verifyPhoneExisting = true;
          this.sendSecurityCode();
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  sendSecurityCode(provider?: PhoneOtpProvider) {
    const phone = this.verification?.verify_phone || this.form.get('phoneNumber')?.value;
    this.currentPhoneOtpProvider = this.phoneOtpProvider.normalizeProviderForPhone(phone, provider);
    this.isLoading = true;
    this.loaderService.show();
    this.isVerifyingToken = true;
    this.isInvalidToken = false;
    this.documentService
      .sendSecurityCode({
        id: this.verification.id,
        type: this.phoneOtpProvider.securityCodeType(this.currentPhoneOtpProvider)
      })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          setTimeout(() => (this.isVerifyingToken = false), 20000);
          this.authenticationService.clearAllFields();
          this.notyService.success(this.phoneOtpProvider.translation('notification', this.currentPhoneOtpProvider, { instant: true }));
        },
        error: error => {
          setTimeout(() => (this.isVerifyingToken = false), 2000);
          this.errorHandlerService.handle(error);
        }
      });
  }

  sendFallbackSecurityCode() {
    const fallbackProvider = this.phoneOtpProvider.fallbackProvider(this.currentPhoneOtpProvider);

    if (fallbackProvider) {
      this.sendSecurityCode(fallbackProvider);
    }
  }

  submitToken() {
    this.isVerifyingToken = true;
    this.isLoading = true;
    this.isInvalidToken = false;

    this.verificationService
      .checkSecurityVerification({ document_id: this.documentId, id: this.verification.id, securityCode: this.token.value })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.isVerifyingToken = false;
        })
      )
      .subscribe({
        next: response => {
          if (response.verified_at) {
            this.notyService.success(this.translateService.instant('notyService.verificationCodeValidated'));
            this.modal.close(response as ModalResult);
          } else {
            this.isInvalidToken = true;
          }
        },
        error: error => {
          this.isInvalidToken = true;
          this.errorHandlerService.handle(error);
        }
      });
  }

  timeOutForWhatsappVerification() {
    if (this.whatsAppTimer.isCountdownInProgress()) {
      this.whatsAppTimer.stopCountdown();
    }
    this.whatsAppTimer
      .startCountdownTimer()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: time => {
          this.formattedTime = time;
        }
      });
  }

  get formattedPhone() {
    const phone = this.verification?.verify_phone || this.form.get('phoneNumber')?.value;
    return this.maskPhone(phone);
  }

  hasPhoneOtpFallbackProvider() {
    return this.phoneOtpProvider.hasFallbackProvider(this.currentPhoneOtpProvider);
  }

  phoneOtpFallbackProviderLabel() {
    const fallbackProvider = this.phoneOtpProvider.fallbackProvider(this.currentPhoneOtpProvider);
    return fallbackProvider ? this.phoneOtpProvider.providerLabel(fallbackProvider) : '';
  }

  maskPhone(phone: string): string {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    return digits.replace(/^(\d{2})(\d{2})(\d{5})(\d{4}).*/, '+$1 ($2) $3-$4');
  }
}
